import { type ApolloClient, gql, useApolloClient } from "@apollo/client";
import { FinalFormAsyncAutocomplete, type FinalFormAsyncAutocompleteProps } from "@comet/admin";
import { type GQLPackagesQuery, type GQLPackagesQueryVariables } from "@src/graphql.generated";
import { type ReactElement } from "react";
import { type FieldRenderProps } from "react-final-form";

export interface PackageOption {
    id: string;
    name: string;
}
export const packagesQuery = gql`
    query SelectPackages($page: Int, $query: String) {
        packages(page: $page, size: 50, query: $query) {
            nodes {
                id
                name
            }
        }
    }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const loadOptions = async (inputValue: string, client: ApolloClient<any>): Promise<PackageOption[]> => {
    const { data } = await client.query<GQLPackagesQuery, GQLPackagesQueryVariables>({
        query: packagesQuery,
        variables: {
            query: inputValue,
        },
    });
    return data.packages.nodes.map(({ id, name }) => ({ id, name }));
};

// `FinalFormAsyncAutocomplete` loads and manages its option list internally from `loadOptions`
// (see @comet/admin's useAsyncAutocompleteOptionsProps). Its props type still marks `options` as
// required (inherited from MUI's Autocomplete), but any `options` value passed by the caller is
// spread *after* the internally loaded ones and therefore overwrites them — so passing
// `options={[]}` leaves the field permanently empty even though the query returns data. Comet's own
// `AsyncAutocompleteField` omits `options` for exactly this reason; we narrow the type to do the same.
type AsyncPackageAutocompleteProps = Omit<FinalFormAsyncAutocompleteProps<PackageOption, false, false, false>, "options"> &
    FieldRenderProps<PackageOption, HTMLInputElement | HTMLTextAreaElement>;

const AsyncPackageAutocomplete = FinalFormAsyncAutocomplete as (props: AsyncPackageAutocompleteProps) => ReactElement;

const PackageSelect = (props: FieldRenderProps<PackageOption, HTMLInputElement | HTMLTextAreaElement>) => {
    const client = useApolloClient();

    return (
        <AsyncPackageAutocomplete
            {...props}
            loadOptions={(inputValue?: string) => loadOptions(inputValue ?? "", client)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
        />
    );
};

export default PackageSelect;
